// pages/organization/ExamResultPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { httpRequest } from "../utils/httpRequest";
import { useTranslation } from "react-i18next";
import { formatAnswerForDisplay } from "../utils/formatAnswerForDisplay";
import { getCorrectAnswer } from "../utils/getCorrectAnswer";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useUserStore } from "../stores/useUserStore";
import { BASE_URL } from "../config";

const clamp3 = {
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const stripHtml = (s = "") => String(s).replace(/<[^>]*>/g, "");

function hasProAiAccess(subscriptions, categoryId) {
  if (!Array.isArray(subscriptions) || !categoryId) return false;

  const now = Date.now();
  const catId = String(categoryId);

  return subscriptions.some((s) => {
    const planOk = String(s?.plan || "").toLowerCase() === "pro";
    const sameCat = String(s?.category?._id || "") === catId;

    const startOk = s?.startAt ? new Date(s.startAt).getTime() <= now : true;
    const endOk = s?.endAt ? new Date(s.endAt).getTime() >= now : false;

    return planOk && sameCat && startOk && endOk;
  });
}

function AiSectionsPanel({ aiSections, examDoc }) {
  const sections = Array.isArray(aiSections) ? aiSections : [];

  const getTitle = (si) => {
    const sec = examDoc?.sections?.[si];
    return (
      sec?.title?.fa ||
      sec?.title?.en ||
      (typeof sec?.title === "string" ? sec.title : "") ||
      `AI Section ${Number(si) + 1}`
    );
  };

  const toFullAudioUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    // BASE_URL مثلاً http://localhost:3001
    return `${BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  if (!sections.length) return null;

  return (
    <div className="mt-10">
      <h2 className="text-lg font-bold text-gray-800 mb-4">
        جزئیات بخش هوش مصنوعی
      </h2>

      <div className="space-y-5">
        {sections
          .slice()
          .sort(
            (a, b) =>
              Number(a?.sectionIndex ?? 0) - Number(b?.sectionIndex ?? 0)
          )
          .map((sec, idx) => {
            const si = Number(sec?.sectionIndex ?? 0);
            const turns = Array.isArray(sec?.turns) ? sec.turns : [];

            const score = Number(sec?.result?.score ?? 0);
            const summary = String(sec?.result?.summary ?? "");
            const mode = sec?.config?.mode || "text";
            const done = sec?.status === "finished";

            return (
              <div
                key={`${si}-${idx}`}
                className="rounded-2xl border bg-gray-50 p-5"
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="font-semibold text-gray-800">
                    {getTitle(si)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {done ? "Finished" : "Active"} • Mode: {mode} • Score:{" "}
                    {score}
                  </div>
                </div>

                {(summary || score) && (
                  <div className="mb-4 rounded-xl border bg-white p-3">
                    <div className="text-sm font-semibold text-gray-800 mb-1">
                      نتیجه
                    </div>
                    <div className="text-sm text-gray-700">
                      <div>
                        <span className="text-gray-500">Score:</span>{" "}
                        <span className="font-mono">{score}</span>
                      </div>
                      {summary ? (
                        <div className="mt-2 whitespace-pre-wrap">
                          {summary}
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  {turns
                    .filter((t) => t?.role !== "system")
                    .map((t, i2) => {
                      const role = t?.role;
                      const isUser = role === "user";
                      const text = (t?.text || "").trim();
                      const audioUrl = toFullAudioUrl(t?.audioUrl);

                      return (
                        <div
                          key={`${si}-turn-${i2}`}
                          className={`max-w-[88%] rounded-xl px-3 py-2 text-sm ${
                            isUser
                              ? "ml-auto bg-indigo-600 text-white"
                              : "mr-auto bg-white border"
                          }`}
                        >
                          <div className="text-[11px] opacity-80 mb-1">
                            {isUser ? "User" : "AI"}
                          </div>

                          {text ? (
                            <div className="whitespace-pre-wrap">{text}</div>
                          ) : (
                            <div className="text-[12px] opacity-70">
                              (متن ندارد)
                            </div>
                          )}

                          {audioUrl ? (
                            <div className="mt-2">
                              <audio
                                controls
                                src={audioUrl}
                                className="w-full"
                              />
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

export default function ExamResultPage() {
  const { resultId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [examDoc, setExamDoc] = useState(null);

  const { t } = useTranslation();

  const [aiLoading, setAiLoading] = useState(false);
  const [aiReport, setAiReport] = useState("");
  const [typedReport, setTypedReport] = useState("");

  const { subscriptions, fetchSubscriptions } = useUserStore();

  const examCategoryId =
    examDoc?.category?._id ||
    examDoc?.categoryId ||
    examDoc?.category ||
    result?.category?._id ||
    result?.categoryId ||
    result?.category ||
    null;

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.token) {
      navigate("/login");
      return;
    }

    (async () => {
      setLoading(true);
      setErr("");
      try {
        const res = await httpRequest(
          "GET",
          `/result/${resultId}`,
          {},
          { "x-access-token": user.token }
        );
        const data = res?.data?.data || res?.data;
        if (!data?._id) throw new Error("Result not found");
        setResult(data);

        const examId = data.exam || data.examId;
        if (examId) {
          const er = await httpRequest(
            "GET",
            `/exam/${examId}`,
            {},
            { "x-access-token": user.token }
          );
          setExamDoc(er?.data?.data || er?.data || null);
        } else {
          setExamDoc(null);
        }
      } catch (e) {
        setErr(e?.message || t("exam_result_page.failed"));
      } finally {
        setLoading(false);
      }
    })();
  }, [resultId, navigate, t]);

  useEffect(() => {
    fetchSubscriptions?.();
  }, [fetchSubscriptions]);

  const canUseAi = useMemo(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user.tags.includes("user")) {
      return hasProAiAccess(subscriptions, examCategoryId);
    } else {
      return true;
    }
  }, [subscriptions, examCategoryId]);

  useEffect(() => {
    if (!aiReport) return;

    setTypedReport("");
    let i = 0;
    const speed = 15;

    const id = setInterval(() => {
      i++;
      setTypedReport(aiReport.slice(0, i));
      if (i >= aiReport.length) clearInterval(id);
    }, speed);

    return () => clearInterval(id);
  }, [aiReport]);

  const itemMap = useMemo(() => {
    const m = new Map();
    (examDoc?.sections || []).forEach((sec) => {
      (sec.items || []).forEach((w) => {
        const it = w?.item || w;
        if (it?._id) m.set(String(it._id), it);
      });
    });
    return m;
  }, [examDoc]);

  async function fetchAiReport() {
    try {
      setAiLoading(true);
      const user = JSON.parse(localStorage.getItem("user"));

      const res = await httpRequest(
        "POST",
        `/ai/result/${resultId}/ai-analysis`,
        {},
        { "x-access-token": user.token }
      );

      const fullText = res.data.analysis || "تحلیلی یافت نشد";
      setAiReport(fullText);
    } catch {
      setAiReport("شما برای امروز به محدودیت استفاده از آپشن های هوش مصنوعی رسیده اید برای استفاده بیشتر فردا برگردید");
    } finally {
      setAiLoading(false);
    }
  }

  const getSectionTitle = (si) => {
    const anyRow = (result?.answers || []).find((a) => a.sectionIndex === si);
    if (anyRow?.sectionTitle) return anyRow.sectionTitle;

    const sec = examDoc?.sections?.[si];
    const title =
      sec?.title?.fa ||
      sec?.title?.en ||
      (typeof sec?.title === "string" ? sec.title : "");
    if (title) return title;

    return t("exam_result_page.section", { index: si + 1 });
  };

  const fmtMMSS = (sec) => {
    if (sec == null || Number.isNaN(sec))
      return t("exam_result_page.placeholder");
    const s = Math.max(0, Math.floor(sec));
    const m = String(Math.floor(s / 60)).padStart(2, "0");
    return `${m}:${String(s % 60).padStart(2, "0")}`;
  };

  const allRows = result?.answers || [];

  const visibleRows = useMemo(() => {
    return allRows.filter((q) => {
      const it = itemMap.get(String(q.question));
      return it?.kind !== "audio_gate";
    });
  }, [allRows, itemMap]);

  const rowsWithSection = useMemo(() => {
    return visibleRows.map((q) => ({
      ...q,
      sectionIndex: Number.isFinite(q.sectionIndex) ? q.sectionIndex : 0,
      sectionTitle: q.sectionTitle || getSectionTitle(q.sectionIndex || 0),
    }));
  }, [visibleRows]); // eslint-disable-line react-hooks/exhaustive-deps

  const groupedBySection = useMemo(() => {
    const m = new Map();
    for (const q of rowsWithSection) {
      const si = q.sectionIndex ?? 0;
      if (!m.has(si)) m.set(si, []);
      m.get(si).push(q);
    }
    for (const [k, arr] of m.entries()) {
      arr.sort((a, b) => (a.order ?? a.idx ?? 0) - (b.order ?? b.idx ?? 0));
    }
    return [...m.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([si, rows]) => ({
        si,
        title:
          rows[0]?.sectionTitle ||
          t("exam_result_page.section", { index: si + 1 }),
        rows,
      }));
  }, [rowsWithSection, t]);

  // ✅ دقیقاً مطابق نمونه‌ای که فرستادی
  const aiSections = useMemo(() => {
    const v =
      result?.aiSections ||
      result?.meta?.aiSections || // اگر جایی اینطوری ذخیره کرده باشی
      result?.meta?.ai?.sections || // اگر مدل Attempt-style هم برگردونی
      [];
    return Array.isArray(v) ? v : [];
  }, [result]);

  if (loading)
    return (
      <div className="p-6 text-center">{t("exam_result_page.loading")}</div>
    );

  if (err) return <div className="p-6 text-center text-rose-600">{err}</div>;
  if (!result) return null;

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-xl shadow mt-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          {t("exam_result_page.page_title")}
        </h1>

        <div className="flex gap-2 items-center">
          <button
            onClick={fetchAiReport}
            disabled={!canUseAi || aiLoading}
            className={`px-4 py-2 rounded-lg text-white ${
              canUseAi
                ? "bg-indigo-600 hover:bg-indigo-700"
                : "bg-gray-300 cursor-not-allowed"
            }`}
            title={
              !canUseAi
                ? "برای تحلیل هوش مصنوعی باید اشتراک پرو همین دسته‌بندی فعال باشد"
                : ""
            }
          >
            {aiLoading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                در حال تحلیل...
              </span>
            ) : (
              "تحلیل هوش مصنوعی"
            )}
          </button>

          <button
            className="flex items-center text-sm text-gray-500 hover:text-indigo-600"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="ml-1" size={16} />
            {t("exam_result_page.back")}
          </button>
        </div>
      </div>

      {(aiLoading || aiReport) && (
        <div className="my-4 p-4 bg-indigo-50 rounded-lg border">
          {aiLoading && (
            <div className="mb-2 text-sm text-gray-500">
              در حال تولید تحلیل توسط هوش مصنوعی...
            </div>
          )}

          {aiReport && (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {typedReport || aiReport}
            </ReactMarkdown>
          )}
        </div>
      )}

      {/* خلاصه */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700 mb-8">
        <div className="space-y-1">
          <p>
            <strong>{t("exam_result_page.title")}</strong>{" "}
            {result.examTitle || t("exam_result_page.placeholder")}
          </p>
          <p>
            <strong>{t("exam_result_page.participant")}</strong>{" "}
            {result.userName || t("exam_result_page.placeholder")}
          </p>
          <p>
            <strong>{t("exam_result_page.start")}</strong>{" "}
            {result.startedAt
              ? new Date(result.startedAt).toLocaleString()
              : t("exam_result_page.placeholder")}
          </p>
          <p>
            <strong>{t("exam_result_page.end")}</strong>{" "}
            {result.finishedAt
              ? new Date(result.finishedAt).toLocaleString()
              : t("exam_result_page.placeholder")}
          </p>
        </div>

        <div className="space-y-1">
          <p>
            <strong>{t("exam_result_page.score")}</strong>{" "}
            <span className="text-indigo-600 font-semibold">
              {result.score ?? 0}
            </span>
          </p>
          <p>
            <strong>{t("exam_result_page.percent")}</strong>{" "}
            <span className="font-semibold">
              {Math.round(result.percentage || 0)}%
            </span>
          </p>
        </div>
      </div>

      {/* ✅ Debug کوچک برای اینکه مطمئن شی دیتا میاد */}
      <div className="mb-6 text-xs text-gray-500">
        aiSections length:{" "}
        <span className="font-mono">{aiSections.length}</span>
      </div>

      {/* جدول پاسخ‌ها */}
      <h2 className="text-lg font-bold text-gray-800 mb-4">
        {t("exam_result_page.answers_detail")}
      </h2>

      {groupedBySection.map(({ si, rows }) => (
        <div key={si} className="mb-8">
          <div className="mb-2 text-sm font-semibold text-gray-700">
            {getSectionTitle(si)}
          </div>

          <div className="overflow-auto rounded-xl border">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-right py-2 px-4 border-b w-12">
                    {t("exam_result_page.col_idx")}
                  </th>
                  <th className="text-right py-2 px-4 border-b">
                    {t("exam_result_page.col_stem")}
                  </th>
                  <th className="text-center py-2 px-4 border-b">
                    {t("exam_result_page.col_your_answer")}
                  </th>
                  <th className="text-center py-2 px-4 border-b">
                    {t("exam_result_page.col_correct")}
                  </th>
                  <th className="text-center py-2 px-4 border-b">
                    {t("exam_result_page.col_score")}
                  </th>
                  <th className="text-center py-2 px-4 border-b">
                    {t("exam_result_page.col_time")}
                  </th>
                  <th className="text-center py-2 px-4 border-b">
                    {t("exam_result_page.col_note")}
                  </th>
                  <th className="text-center py-2 px-4 border-b">
                    {t("exam_result_page.col_attachments")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {rows.map((q, i) => {
                  const item = itemMap.get(String(q.question));

                  const stemHtml =
                    item?.stem?.en ||
                    item?.stem?.fa ||
                    item?.body?.text?.en ||
                    item?.body?.text?.fa ||
                    "";

                  const manualComment = q?.manualComment;
                  const userAnsText = formatAnswerForDisplay(item, q.answer, t);

                  const correctRaw = getCorrectAnswer(item);
                  let corrAnsText = t("exam_result_page.placeholder");

                  if (
                    correctRaw &&
                    typeof correctRaw === "object" &&
                    correctRaw.__type === "sent_rec"
                  ) {
                    corrAnsText =
                      correctRaw.first || t("exam_result_page.placeholder");
                  } else if (
                    correctRaw &&
                    typeof correctRaw === "object" &&
                    correctRaw.__type === "ctest"
                  ) {
                    corrAnsText = (correctRaw.list || [])
                      .map((p) => `${p.key}: ${p.ans || "…"}`)
                      .join(" | ");
                  } else if (
                    correctRaw &&
                    typeof correctRaw === "object" &&
                    correctRaw.__type === "ordering"
                  ) {
                    corrAnsText = (correctRaw.seq || []).join(" \u2192 ");
                  } else if (
                    correctRaw &&
                    typeof correctRaw === "object" &&
                    correctRaw.__type === "slot"
                  ) {
                    corrAnsText = `${t("exam_result_page.slot")} #${
                      Number(correctRaw.value) + 1
                    }`;
                  } else if (Array.isArray(correctRaw)) {
                    corrAnsText = correctRaw.join(", ");
                  } else if (correctRaw != null) {
                    corrAnsText = String(correctRaw);
                  }

                  const earned = Number(q.rowScore ?? 0);
                  const maxPts = Number(item?.points ?? 0);
                  const time = q.timeSpent ?? q.timeSec ?? 0;
                  const isCorrect = !!q.isCorrect;

                  return (
                    <tr
                      key={`${q._id || q.question}-${si}-${i}`}
                      className="border-b align-top"
                    >
                      <td className="py-3 px-4 text-right text-gray-500">
                        {i + 1}
                      </td>

                      <td className="py-3 px-4">
                        {stemHtml ? (
                          <div
                            className="prose prose-sm max-w-none max-h-16 overflow-hidden"
                            style={clamp3}
                            dangerouslySetInnerHTML={{
                              __html: stripHtml(stemHtml),
                            }}
                          />
                        ) : (
                          <span className="text-gray-400">
                            {t("exam_result_page.placeholder")}
                          </span>
                        )}

                        {item?.kind ? (
                          <div className="mt-1 text-[11px] text-gray-500">
                            نوع: {t(`item_kinds.${item.kind}`)}
                          </div>
                        ) : null}
                      </td>

                      <td className="py-3 px-4 text-center">
                        <div
                          style={clamp3}
                          className={
                            isCorrect
                              ? "text-emerald-700 font-semibold"
                              : "text-rose-600 font-semibold"
                          }
                          title={userAnsText}
                        >
                          {userAnsText}
                        </div>
                      </td>

                      <td className="py-3 px-4 text-center">
                        <div style={clamp3} title={corrAnsText} dir="ltr">
                          {corrAnsText}
                        </div>
                      </td>

                      <td className="py-3 px-4 text-center">
                        <span className="font-mono">
                          {earned}/{maxPts}
                        </span>
                      </td>

                      <td className="py-3 px-4 text-center">{fmtMMSS(time)}</td>

                      <td className="py-3 px-4">
                        <span className="text-gray-400 ">
                          {manualComment
                            ? manualComment
                            : t("exam_result_page.placeholder")}
                        </span>
                      </td>

                      <td className="py-3 px-4 text-center">
                        <span className="text-gray-400">
                          {t("exam_result_page.placeholder")}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* ✅ این بخش دقیقا از result.aiSections می‌خونه */}
      <AiSectionsPanel aiSections={aiSections} examDoc={examDoc} />
    </div>
  );
}
